import React, { ReactNode, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { useOktaAuth } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import type { OidcAppState } from '@/tools/auth/oidcConfig.ts';
import { environment } from '@/environment.ts';
import { FullscreenLoadingSpinner } from '@/shared/components/FullscreenLoadingSpinner.tsx';
import { useAuthTokenContext } from '@/shared/context/AuthTokenContext.tsx';
import { PostAuthenticationInitialization } from '@/routes/PostAuthenticationInitialization.tsx';
import { HttpClientContextProvider } from '@/shared/context/HttpClientContext.tsx';
import { MarketContextProvider, useMarketContext } from '@/shared/context/MarketContext.tsx';
import { useSearchParams } from 'react-router';
import { MarketCodeSchema } from '@schuettflix/utils';
import { SentryUserProvider } from '@/tools/sentry/SentryUserProvider.tsx';

function HttpClientContextProviderWithMarketFromContext({ children }: { children: ReactNode }) {
    const { marketCode } = useMarketContext();

    return <HttpClientContextProvider marketCode={marketCode}>{children}</HttpClientContextProvider>;
}

function EnhancedOutlet() {
    const { getDefaultMarketCode, isMarketAvailable } = useAuthTokenContext();
    const [searchParams] = useSearchParams();

    const parsedMarketCode = MarketCodeSchema.safeParse(searchParams.get('marketCode'));
    const marketCode =
        parsedMarketCode.success && isMarketAvailable(parsedMarketCode.data)
            ? parsedMarketCode.data
            : getDefaultMarketCode();

    return (
        <>
            <SentryUserProvider />
            <MarketContextProvider marketCode={marketCode}>
                <HttpClientContextProviderWithMarketFromContext>
                    <PostAuthenticationInitialization />
                </HttpClientContextProviderWithMarketFromContext>
            </MarketContextProvider>
        </>
    );
}

function OAuthAuthGuard() {
    // Guards the authenticated routes on the OAuth path: triggers the OAuth login redirect
    // when the user is not authenticated, and feeds the access token (which carries the
    // market and identity claims — email, given_name, family_name) into AuthTokenContext so
    // the rest of the app reads from it.
    const auth = useAuth();
    const { initialize, isInitialized } = useAuthTokenContext();
    const hasTriedSignin = useRef(false);

    useEffect(() => {
        if (auth.isLoading || auth.activeNavigator || auth.error || auth.isAuthenticated) {
            return;
        }

        if (!hasTriedSignin.current) {
            hasTriedSignin.current = true;
            auth.signinRedirect({
                state: { returnTo: window.location.pathname + window.location.search } satisfies OidcAppState,
            }).catch(error => {
                console.error('Error while redirecting to login', error);
            });
        }
    }, [auth]);

    useEffect(() => {
        if (auth.isAuthenticated && auth.user?.access_token) {
            initialize(auth.user.access_token);
        }
    }, [auth.isAuthenticated, auth.user, initialize]);

    useEffect(() => {
        // automaticSilentRenew renews the access token in the background using the refresh
        // token. A renew can fail transiently WITHOUT the session being lost: the server
        // rotates refresh tokens, so a second concurrent renew (e.g. another open tab, or a
        // StrictMode double-mount in dev) reuses an already-rotated token and gets "token
        // revoked" while a valid access token still exists. Only fall back to a full login
        // when the session is genuinely gone (no user, or the current token has expired);
        // otherwise ignore and let the next renew cycle recover. Preserve the location so the
        // user lands back where they were.
        return auth.events.addSilentRenewError(() => {
            const sessionLost = !auth.user || auth.user.expired === true;
            if (!sessionLost) {
                console.warn('OAuth silent renew failed but a valid token remains; ignoring.');
                return;
            }
            auth.signinRedirect({
                state: { returnTo: window.location.pathname + window.location.search } satisfies OidcAppState,
            }).catch(error => {
                console.error('Error while redirecting to login after silent renew failure', error);
            });
        });
    }, [auth]);

    // While a recovery redirect is in flight (activeNavigator set) keep the spinner rather
    // than flashing the error alert; only surface the alert for a terminal failure.
    if (auth.error && !auth.activeNavigator) {
        return <div role="alert">Authentication failed: {auth.error.message}</div>;
    }

    if (auth.isAuthenticated && !auth.user?.access_token) {
        return <div role="alert">Authentication succeeded but no access token was returned.</div>;
    }

    if (!auth.isAuthenticated || !isInitialized()) {
        return <FullscreenLoadingSpinner />;
    }

    return <EnhancedOutlet />;
}

function OktaAuthGuard() {
    // Guards the authenticated routes on the Okta path: triggers the Okta login redirect
    // when unauthenticated, and feeds the Okta ID token into AuthTokenContext.
    const { oktaAuth, authState } = useOktaAuth();
    const { initialize, isInitialized } = useAuthTokenContext();

    useEffect(() => {
        if (!authState) {
            return;
        }

        if (!authState?.isAuthenticated) {
            const originalUri = toRelativeUrl(window.location.href, window.location.origin);
            oktaAuth.setOriginalUri(originalUri);
            oktaAuth.signInWithRedirect().catch(error => {
                console.error('Error while redirecting to login', error);
            });
        }
    }, [oktaAuth, authState, authState?.isAuthenticated]);

    useEffect(() => {
        if (authState && authState.idToken) {
            initialize(authState.idToken.idToken);
        }
    }, [authState, authState?.idToken, initialize]);

    if (!isInitialized()) {
        return <FullscreenLoadingSpinner />;
    }

    return <EnhancedOutlet />;
}

export function AuthGuard() {
    // Select the login path by env flag so we can flip Okta/OAuth per environment.
    return environment.AUTH_PROVIDER === 'oauth' ? <OAuthAuthGuard /> : <OktaAuthGuard />;
}
